import { transposeChord, accidentalPreference } from './chordTranspose';

/**
 * Parse a plain-text chord sheet (as exported by exportText) back into
 * { title, songKey, capo, lyrics, alignment }.
 *
 * Format expected:
 *   Line 1 (optional): Song title (if it's not a metadata or section line)
 *   Line 2 (optional): "Key: G   Capo: 2" metadata
 *   Blank line separator
 *   [Section Name]
 *   G       Am        C
 *   Lyric line here
 *   ...
 *
 * Chord rows are detected by checking every non-blank token against a chord pattern.
 */

// Regex that matches common chord names, e.g. C, Am, G#m, Bbmaj7, F#7/C#, Dsus4, etc.
const CHORD_RE = /^[A-G][#b]?(m(?:aj)?|min|dim|aug|sus[24]?|add\d*|M)?(\d+)?(\/[A-G][#b]?)?$/;

function isChordToken(token) {
  return CHORD_RE.test(token);
}

function isChordRow(line) {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return false;
  return tokens.every(isChordToken);
}

/**
 * Parse chord positions from a chord row relative to a lyric line.
 * Returns [{chord, wordIndex}] where wordIndex corresponds to the closest word start.
 */
function parseChordRow(chordLine, lyricLine) {
  // Find each chord and its character position in the chord row
  const chordEntries = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(chordLine)) !== null) {
    chordEntries.push({ chord: m[0], pos: m.index });
  }

  if (!lyricLine || !lyricLine.trim()) {
    // No lyric — map wordIndex sequentially starting at 0
    return chordEntries.map((e, i) => ({ chord: e.chord, wordIndex: i }));
  }

  // Build word positions for the lyric line
  const wordPositions = [];
  const wordRe = /\S+/g;
  while ((m = wordRe.exec(lyricLine)) !== null) {
    wordPositions.push({ index: wordPositions.length, start: m.index });
  }

  if (wordPositions.length === 0) {
    return chordEntries.map((e, i) => ({ chord: e.chord, wordIndex: i }));
  }

  return chordEntries.map(({ chord, pos }) => {
    // Find the last word whose start ≤ pos (prevWi) and first word after pos (nextWiIdx)
    let prevWi = -1;
    let nextWiIdx = wordPositions.length;
    for (let i = 0; i < wordPositions.length; i++) {
      if (wordPositions[i].start <= pos) {
        prevWi = i;
      } else {
        nextWiIdx = i;
        break;
      }
    }

    if (prevWi === -1) {
      // Before all words — attach to first word
      return { chord, wordIndex: 0 };
    }

    if (wordPositions[prevWi].start === pos) {
      // Chord column exactly matches a word start → integer anchor
      return { chord, wordIndex: prevWi };
    }

    // Chord is after word prevWi's column but before the next word → spacer
    // Assign a fractional wordIndex based on its position in the inter-word gap
    // so multiple spacers in the same gap get distinct non-colliding values.
    const prevWordCol = wordPositions[prevWi].start;
    const nextWordCol = nextWiIdx < wordPositions.length
      ? wordPositions[nextWiIdx].start
      : prevWordCol + 20;
    const fraction = (pos - prevWordCol) / (nextWordCol - prevWordCol);
    return { chord, wordIndex: prevWi + Math.min(fraction, 0.99) };
  });
}

// Sections where a chord row immediately after the header belongs to the header itself
// (no lyric content — chords fill the whole section)
const HEADER_CHORD_SECTIONS = ['intro', 'outro', 'instrumental', 'solo'];

export function parseChordSheet(text) {
  const rawLines = text.split('\n');
  let lineIdx = 0;

  // ── Extract title (first non-blank line that isn't metadata or section) ──
  let title = '';
  while (lineIdx < rawLines.length && rawLines[lineIdx].trim() === '') lineIdx++;
  const firstLine = rawLines[lineIdx]?.trim() || '';
  if (firstLine && !/^\[/.test(firstLine) && !/^Key:/i.test(firstLine) && !isChordRow(firstLine)) {
    title = firstLine;
    lineIdx++;
  }

  // ── Extract key / capo / bpm metadata ───────────────────────────────────
  // Format: "Key: F  BPM: 120" on one line, optionally "Capo: 1" on the next
  let songKey = '';
  let capo = 0;
  let bpm = 0;
  while (lineIdx < rawLines.length && rawLines[lineIdx].trim() === '') lineIdx++;
  const metaLine = rawLines[lineIdx]?.trim() || '';
  const keyMatch = metaLine.match(/Key:\s*([A-G][#b]?(?:\s*(?:m(?:aj(?:or)?)?|min(?:or)?|major))?)/i);
  const capoMatch = metaLine.match(/Capo:\s*(\d+)/i);
  const bpmMatch = metaLine.match(/BPM:\s*(\d+)/i);
  if (keyMatch || capoMatch || bpmMatch) {
    if (keyMatch) songKey = keyMatch[1].trim();
    if (capoMatch) capo = parseInt(capoMatch[1], 10);
    if (bpmMatch) bpm = parseInt(bpmMatch[1], 10);
    lineIdx++;
    // Check next line for a standalone Capo line (new two-line format)
    const nextLine = rawLines[lineIdx]?.trim() || '';
    const capoLine = nextLine.match(/^Capo:\s*(\d+)$/i);
    if (capoLine) {
      capo = parseInt(capoLine[1], 10);
      lineIdx++;
    }
  }

  // ── Parse body into lyrics + alignment ───────────────────────────────────
  // Skip any blank separator lines before the first section
  while (lineIdx < rawLines.length && rawLines[lineIdx].trim() === '') lineIdx++;

  const lyricLines = [];
  const alignment = [];
  let pendingChordRow = null;
  // Track last section header to detect header-level chord rows (e.g. [Intro] C  F  G)
  let lastSectionHeaderLi = null;
  let lastSectionName = '';

  for (; lineIdx < rawLines.length; lineIdx++) {
    const raw = rawLines[lineIdx];
    const trimmed = raw.trim();

    if (trimmed === '') {
      if (pendingChordRow !== null) {
        // Chord row followed by blank line: the blank line is its "lyric" (no double-blank)
        const li = lyricLines.length;
        lyricLines.push('');
        const chords = parseChordRow(pendingChordRow.raw, '');
        chords.forEach(({ chord, wordIndex }) => {
          alignment.push({ chord, lineIndex: li, wordIndex });
        });
        pendingChordRow = null;
      } else {
        lyricLines.push('');
      }
      lastSectionHeaderLi = null;
      continue;
    }

    // Section header line [Verse], [Chorus], etc.
    if (/^\[.+\]/.test(trimmed)) {
      if (pendingChordRow !== null) {
        // Chord row immediately before a section header — flush to empty line
        const li = lyricLines.length;
        lyricLines.push('');
        const chords = parseChordRow(pendingChordRow.raw, '');
        chords.forEach(({ chord, wordIndex }) => {
          alignment.push({ chord, lineIndex: li, wordIndex });
        });
        pendingChordRow = null;
      }
      const sectionHeaderLi = lyricLines.length;
      lyricLines.push(trimmed);
      lastSectionHeaderLi = sectionHeaderLi;
      lastSectionName = trimmed.replace(/^\[|\]$/g, '').toLowerCase().replace(/\s*\d+\s*$/, '').trim();
      continue;
    }

    // Check if this is a chord-only row
    if (isChordRow(trimmed)) {
      // Chord row immediately after [Intro]/[Outro]/[Instrumental] → header-level
      let isHeaderSection = false;
      for (let si = 0; si < HEADER_CHORD_SECTIONS.length; si++) {
        if (lastSectionName.includes(HEADER_CHORD_SECTIONS[si])) {
          isHeaderSection = true;
          break;
        }
      }
      if (lastSectionHeaderLi !== null && isHeaderSection) {
        let targetLi;
        if (lastSectionName.includes('intro')) targetLi = -1;
        else if (lastSectionName.includes('outro')) targetLi = -2;
        else targetLi = lastSectionHeaderLi; // [Instrumental] → header's lyricLine index
        const chords = parseChordRow(raw, '');
        chords.forEach(({ chord, wordIndex }) => {
          alignment.push({ chord, lineIndex: targetLi, wordIndex });
        });
        lastSectionHeaderLi = null;
        continue;
      }

      if (pendingChordRow !== null) {
        // Two chord rows in a row — flush previous onto an empty line
        const li = lyricLines.length;
        lyricLines.push('');
        const chords = parseChordRow(pendingChordRow.raw, '');
        chords.forEach(({ chord, wordIndex }) => {
          alignment.push({ chord, lineIndex: li, wordIndex });
        });
      }
      pendingChordRow = { raw, trimmed };
      lastSectionHeaderLi = null;
      continue;
    }

    // Regular lyric line
    lastSectionHeaderLi = null;
    const li = lyricLines.length;
    // Parse chord positions against the original (possibly wide-spaced) lyric so that
    // chord column positions map correctly to word positions.
    // Store the lyric with collapsed spaces so the display doesn't add extra visual gaps
    // on top of the spacer-chord blank spans that the renderer inserts.
    const normalizedLyric = trimmed.replace(/  +/g, ' ');
    lyricLines.push(normalizedLyric);
    if (pendingChordRow !== null) {
      const chords = parseChordRow(pendingChordRow.raw, trimmed);
      chords.forEach(({ chord, wordIndex }) => {
        alignment.push({ chord, lineIndex: li, wordIndex });
      });
      pendingChordRow = null;
    }
  }

  // Flush any trailing chord row
  if (pendingChordRow !== null) {
    const li = lyricLines.length;
    lyricLines.push('');
    const chords = parseChordRow(pendingChordRow.raw, '');
    chords.forEach(({ chord, wordIndex }) => {
      alignment.push({ chord, lineIndex: li, wordIndex });
    });
  }

  // Remove leading/trailing blank lines
  while (lyricLines.length && lyricLines[0] === '') lyricLines.shift();
  while (lyricLines.length && lyricLines[lyricLines.length - 1] === '') lyricLines.pop();

  // ── Un-apply capo: exported chords are capo-shapes; reverse to sounding pitches ──
  // The app will re-apply the capo (set below) when displaying, giving correct shapes back.
  if (capo > 0) {
    const pref = accidentalPreference(songKey);
    alignment.forEach(a => {
      a.chord = transposeChord(a.chord, capo, pref);
    });
  }

  return { title, songKey, capo, bpm, lyrics: lyricLines.join('\n'), alignment };
}
